import { useMemo, useState } from "react";
import { ChevronRightIcon } from "lucide-react";

import { FilterDefinitionType } from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { toTestId } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.tools";
import { FilterDefinitionRow } from "@/components/organisms/UtilityBar/FilterChip/FilterChipDefinitionRow";

export type FilterDefinitionWithKey = FilterDefinitionType & {
  key: string;
  isDivider?: boolean;
};

/**
 * A record the rows point at (``Document``, for ``documents.type``): one row of
 * the list, whose filters open beside it.
 */
type RelatedRecordWithKey = {
  key: string;
  name: string;
  ReactIcon: FilterDefinitionType["ReactIcon"];
  filterDefinitions: FilterDefinitionWithKey[];
};

type FilterDefinitionListRow = FilterDefinitionWithKey | RelatedRecordWithKey;

/**
 * The filters a chip can be: the list's own, then one row for each record its
 * rows point at, whose filters open in a popper to its right. Adding a chip
 * and changing what one filters on pick from the same list.
 */
export function FilterChipDefinitionList({
  filterDefinitions,
  onPick,
  testIdPrefix,
  className,
}: {
  filterDefinitions: Record<string, FilterDefinitionType>;
  onPick: (filterDefinition: FilterDefinitionWithKey) => void;
  /** ``filter-definition`` when adding a chip, ``filter-name`` when changing one. */
  testIdPrefix: string;
  className?: string;
}) {
  const rows = useMemo(() => {
    const own: FilterDefinitionListRow[] = [];
    const related = new Map<string, RelatedRecordWithKey>();
    for (const [key, filterDefinition] of Object.entries(filterDefinitions)) {
      if (!filterDefinition.group) {
        own.push({ key, ...filterDefinition });
        continue;
      }
      const record = related.get(filterDefinition.group) ?? {
        key: `related:${filterDefinition.group}`,
        name: filterDefinition.group,
        ReactIcon: filterDefinition.ReactIcon,
        filterDefinitions: [],
      };
      // The record itself (``documents``) is named after it; it lends the row its icon.
      if (filterDefinition.name === filterDefinition.group) {
        record.ReactIcon = filterDefinition.ReactIcon;
      }
      record.filterDefinitions.push({ key, ...filterDefinition });
      related.set(filterDefinition.group, record);
    }
    return [...own, ...related.values()];
  }, [filterDefinitions]);
  const [openRecordKey, setOpenRecordKey] = useState<string | null>(null);

  const pick = (filterDefinition: FilterDefinitionWithKey) => {
    setOpenRecordKey(null);
    onPick(filterDefinition);
  };

  return (
    <List<FilterDefinitionListRow>
      data={rows}
      onItemClick={(row) => {
        if ("filterDefinitions" in row) {
          setOpenRecordKey((open) => (open === row.key ? null : row.key));
          return;
        }
        pick(row);
      }}
      className={className}
    >
      <List.Items<FilterDefinitionListRow>>
        {(row: FilterDefinitionListRow) =>
          "filterDefinitions" in row ? (
            <List.Item
              value={row}
              data-testid={`${testIdPrefix}-record-${toTestId(row.name)}`}
            >
              <Popper
                placement="right-start"
                isClickable={false}
                isEscapeable={true}
                isOpenState={[
                  openRecordKey === row.key,
                  (open) => setOpenRecordKey(open ? row.key : null),
                ]}
                anchor={
                  <FilterDefinitionRow
                    filterDefinition={{ ...row, type: "reference" }}
                    trailing={
                      <Icon
                        ReactIcon={ChevronRightIcon}
                        size="sm"
                        style={{
                          color:
                            "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
                        }}
                      />
                    }
                  />
                }
              >
                {/* Portaled, but still inside the row above: a pick here is not a click on it. */}
                <div onClick={(event) => event.stopPropagation()}>
                  <List<FilterDefinitionWithKey>
                    data={row.filterDefinitions}
                    onItemClick={pick}
                    className="max-h-100"
                  >
                    <List.Items<FilterDefinitionWithKey>>
                      {(filterDefinition: FilterDefinitionWithKey) => (
                        <List.Item
                          value={filterDefinition}
                          data-testid={`${testIdPrefix}-option-${toTestId(filterDefinition.name)}`}
                        >
                          <FilterDefinitionRow
                            isCompact
                            filterDefinition={{
                              ...filterDefinition,
                              // Beside its record, a filter goes by its own name: "Contact", not "Document → Contact".
                              name: filterDefinition.name.startsWith(
                                `${row.name} → `,
                              )
                                ? filterDefinition.name.slice(
                                    `${row.name} → `.length,
                                  )
                                : filterDefinition.name,
                            }}
                          />
                        </List.Item>
                      )}
                    </List.Items>
                  </List>
                </div>
              </Popper>
            </List.Item>
          ) : (
            <List.Item
              value={row}
              data-testid={`${testIdPrefix}-option-${toTestId(row.name)}`}
            >
              <FilterDefinitionRow filterDefinition={row} />
            </List.Item>
          )
        }
      </List.Items>
    </List>
  );
}
